import { PrimitiveAtom, atom } from 'jotai';

import { MissingCVTypeParams } from '@restworld/utils-common';
import type { JobApplication } from '@restworld/utility-types';

type ActiveTabType = 'application-wizard' | 'upload-cv' | '';
type ApplicationType = 'job-apply' | 'spontaneous-apply';

export interface IErrorProps extends Required<MissingCVTypeParams> {
  statusCode?: number;
  user_id?: string;
  code?: string;
  // Sent by the backend on a refusal that names its cause, e.g. `cv_multiple_profiles`.
  error_code?: string;
  message?: string;
  profiles_count?: number;
}
type SpontaneousApplyDataType = {
  isSpontaneousEntry?: boolean;
  applicationType?: ApplicationType;
  restaurantID?: string;
  didUserSpontaneousApply?: boolean;
  showCoverLetter?: boolean;
  entryType?: ActiveTabType;
};
interface WorkerEntryPointType {
  uploadCVLoading: boolean;
  isCVUploaded: boolean;
  workerId: string;
  errors: Partial<IErrorProps>;
  uploadFiles: FileList | File[];
  isCVUploadProcessCompleted: boolean;
  doesSizeExceeds: boolean;
  activeTab?: ActiveTabType;
  showConfirmModal: boolean;
  spontaneousApplyData?: SpontaneousApplyDataType;
  jobApplicationModalAlreadyDisplayed: boolean;
}

export const uploadCVLoadingAtom = atom<WorkerEntryPointType['uploadCVLoading']>(false);
export const isCVUploadedAtom = atom<WorkerEntryPointType['isCVUploaded']>(false);
export const workerIdAtom = atom<WorkerEntryPointType['workerId']>('');
export const workerEntryPointErrorsAtom = atom<WorkerEntryPointType['errors']>({});
workerEntryPointErrorsAtom.debugLabel = 'workerEntryPointErrorsAtom';
export const uploadFilesAtom = atom<WorkerEntryPointType['uploadFiles']>([]);
export const isCVUploadProcessCompletedAtom = atom<WorkerEntryPointType['isCVUploadProcessCompleted']>(false);
export const doesSizeExceedsAtom = atom<WorkerEntryPointType['doesSizeExceeds']>(false);
export const activeTabAtom = atom<WorkerEntryPointType['activeTab']>('');
export const showConfirmModalAtom = atom<WorkerEntryPointType['showConfirmModal']>(false);
export const spontaneousApplyDataAtom = atom<WorkerEntryPointType['spontaneousApplyData']>({});
export const jobApplicationModalAlreadyDisplayedAtom =
  atom<WorkerEntryPointType['jobApplicationModalAlreadyDisplayed']>(false);

export const resetWorkerEntryStates = atom(null, (_, set) => {
  set(uploadCVLoadingAtom, false);
  set(isCVUploadedAtom, false);
  set(workerEntryPointErrorsAtom, {});
  set(uploadFilesAtom, []);
});
export const doesPhoneNumberExistAtom = atom<boolean>(false);

/**
 * Which of the two in-app assessment confirmations is on screen, or `null` for every
 * application that is not assessed in the app, which keeps the confirmation it has today.
 *
 * `new` and `returning` are not a property of the worker: they are already decided by WHICH
 * screen the flow ends on, and each screen sets this itself.
 *   - `returning` is set by `JobApplyConfirmation`, reachable only from an already
 *     authenticated session (see `showConfirmModalAtom`, set in two places, both behind
 *     `isAuthenticated`).
 *   - `new` is set by the CV upload flow, which is the path of a worker who had no account
 *     when the flow started.
 *
 * It exists because the confirmation body cannot reach the modal chrome around it: the close
 * X, the backdrop/Escape dismissal and the fixed footer all belong to `WorkerEntryPointModal`.
 * Both writers clear it on unmount; a stale value would open the NEXT modal without its X.
 */
export type InAppAssessmentConfirmation = 'new' | 'returning';
// `as PrimitiveAtom` for the same reason as `showViolaBadgeAtom` above: with a `null` initial
// value jotai's overloads infer a read-only atom and `useSetAtom` stops type-checking.
export const inAppAssessmentConfirmationAtom = atom<InAppAssessmentConfirmation | null>(
  null
) as PrimitiveAtom<InAppAssessmentConfirmation | null>;

/**
 * The application the CV upload flow just created, as the apply request handed it back.
 *
 * `JobApplyConfirmation` keeps its own in component state, because it both makes the request
 * and draws the result. The CV flow cannot: the request is fired by `useEntryPointModal` and
 * the result is drawn by `EntryPointModalBody`, which are not in the same tree.
 *
 * Only `assessment_channel` is read from it. Absent or null, which is every application
 * created before the in-app rollout, means the CV flow ends on exactly the screen it ends on
 * today.
 */
export const createdJobApplicationAtom = atom<Partial<JobApplication> | undefined>(undefined) as PrimitiveAtom<
  Partial<JobApplication> | undefined
>;
